import type {Page} from '@PosterWhiteboard/page/page.class';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {PosterHistory} from '@PosterWhiteboard/poster/poster-history/poster-history';
import type {GettyAssetClassification, GettyLimitFilterResult, PosterEvents, PosterLoadObject, PosterObject, UploadingImageEntry} from '@PosterWhiteboard/poster/poster.types';
import {GettyAssetType, POSTER_GETTY_LIMIT, POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {SavePoster} from '@PosterWhiteboard/save-poster/save-poster.class';
import {User} from '@PosterWhiteboard/user/user.class';
import {PosterType} from '@PosterWhiteboard/poster-type/poster-type.class';
import {Observable} from '@PosterWhiteboard/observable';
import {PosterPages} from '@PosterWhiteboard/poster/poster-pages.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {PosterDrawing} from '@PosterWhiteboard/poster/poster-drawing/poster-drawing';
import {PosterMediaCrop} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.class';
import {Grid} from '@PosterWhiteboard/poster/grid.class';
import {Bleed} from '@PosterWhiteboard/poster/bleed.class';
import {Fold} from '@PosterWhiteboard/poster/fold.class';
import {PosterRedux} from '@PosterWhiteboard/poster/poster-redux.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import PosterScaling from '@PosterWhiteboard/poster/poster-scaling.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {PosterModeDetails, PosterModeObject} from '@PosterWhiteboard/poster/poster-mode.class';
import {PosterMode, PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {getUniqueString} from '@Utils/string.util';
import {ResizePoster} from '@PosterWhiteboard/poster/resize-poster.class';
import type {RGBA} from '@Utils/color.util';
import {DEFAULT_FILL_COLOR} from '@PosterWhiteboard/classes/fill.class';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {CornerPointsArray} from '@Utils/math.util';
import {isIOS, isSafari} from 'react-device-detect';
import {PosterVideoGeneration} from '@PosterWhiteboard/poster/poster-video-generation.class';
import {ItemsMultiSelect} from '@PosterWhiteboard/poster/items-multi-select.class';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {config, getFilterBackend, WebGLFilterBackend} from '@postermywall/fabricjs-2';
import {getPosterBackendObjectFromPoster} from '@PosterWhiteboard/poster/poster-frontend-to-backend';
import type {PosterObjectBackend} from '@PosterWhiteboard/poster/poster-backend.types';
import type {DeepPartial} from '@/global';
import type {FolderObject} from '../classes/folder.class';
import {Folder} from '../classes/folder.class';
import Emitter from '@/services/event-emitter';
import {EmbeddedEditorMessageType} from '@Libraries/embedded-editor';
import {isUserPremium} from '@Libraries/user.library';
import {initDefaultFabricControls} from '@PosterWhiteboard/poster/poster-item-controls';
import {resizePosterForResizeData} from '@PosterWhiteboard/libraries/resize-poster.library';
import type {PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import {notifyPuppeteer} from '@Libraries/puppeeter.library';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import {updateIsAutosaveEnabled} from '@/reducers/user-preferences.reducer';
import '@PosterWhiteboard/items/item/item';
import type {TranscriptItem} from '@PosterWhiteboard/items/transcript-item/transcript-item';
import {PosterPreview} from '@PosterWhiteboard/save-poster/poster-preview.class';
import {isPosterVideo} from '@Libraries/poster-object.library';
import {Howler} from 'howler';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import debounce from 'lodash/debounce';
import {getChildElementWithMaxVisibleHeight, getVisibleHeightOfElement} from '@Utils/dom.util';
import {getWhiteboardOccludedBottomHeight} from '@PosterWhiteboard/page/page-visibility.util';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {TimelineAppendSourceCredit} from '@PosterWhiteboard/page/append-pages-to-timeline';
import {appendPageObjectsToPageTimeline} from '@PosterWhiteboard/page/append-pages-to-timeline';
import {trackTimelineCloseIfOpen} from '@Libraries/poster-editor-tracking.library';

const WEBGL_TEXTURE_SIZE = 4096;
const GENERATION_WEBGL_TEXTURE_SIZE = 10800;
const UPDATE_FROM_OBJECT_KEY = 'poster-update-from-object';
const TEMPLATE_PROCESSING_KEY = 'poster-template-processing';
export const MAX_NUMBER_OF_PAGES = window.PMW.MAX_NUMBER_OF_PAGES;
const ADD_DESIGN_PAGES_GROWL_KEY = 'add-design-pages-progress';
const ADD_DESIGN_PAGES_GROWL_INTERVAL = 60000;
const ADD_DESIGN_PAGES_GROWL_REMOVE_DELAY = 500;

function dismissAddDesignPagesGrowl(): void {
  getPosterUi().hideMessageGrowl(ADD_DESIGN_PAGES_GROWL_KEY);
  setTimeout(() => {
    getPosterUi().removeMessageGrowl(ADD_DESIGN_PAGES_GROWL_KEY);
  }, ADD_DESIGN_PAGES_GROWL_REMOVE_DELAY);
}

/**
 * Reads the global whiteboard (not `this`) on purpose: headless/generation contexts have no
 * registered editor whiteboard and must never enter playback through this gate.
 */
function canPosterBePlayed(): boolean {
  const whiteboard = window.posterEditor?.whiteboard;
  return !!whiteboard?.isVideo() || !!whiteboard?.mode.isWebpage();
}

export interface PosterOpts {
  mode: PosterModeObject;
  savedPosterPreviewUrls: string[];
  folder?: FolderObject;
  isHighRes?: boolean;
  ageOfGeneratedOrder?: number;
  posterBackendObject?: PosterObjectBackend;
}

interface ReplacePosterOpts {
  maintainDimensions?: boolean;
  undoable?: boolean;
  showResizeSuccessMessage?: boolean;
}

interface ReplacePosterWithTemplateOpts {
  maintainDimensions?: boolean;
  undoable?: boolean;
  showResizeSuccessMessage?: boolean;
  originalTemplatePageOrder?: string[];
  setTemplateData?: boolean;
  templatePosterId?: string;
  clearGalleryTemplateData?: boolean;
}

export class Poster extends Observable<PosterEvents> {
  public id = '';
  public hashedID = '';
  public name = '';
  public description = '';
  public folder: Folder;
  public isTemplate = false;
  public isPublic = false;
  public isCopyable = true;
  public isPurchaseable = true;
  public createdOn = 0;
  public lastModified = 0;
  public idLastModifier = '';
  public isInternal = false;

  public seoName = '';
  public version: number = POSTER_VERSION.CURRENT;
  public height!: number;
  public width!: number;
  public type!: PosterType;
  public units = '';
  public creator?: User;
  public owner?: User;
  public userWidth = 0;
  public userHeight = 0;
  public uploadingImages: UploadingImageEntry[] = [];
  public pages: PosterPages;

  public HTMLElement: HTMLDivElement;
  private activePageId = '';
  public selectedItemPageId: string | undefined;
  public isHighRes = false;
  public ageOfGeneratedOrder?: number;
  public mode: PosterMode;
  public drawing: PosterDrawing;
  public mediaCrop: PosterMediaCrop;
  public itemsMultiSelect: ItemsMultiSelect;
  public grid: Grid;
  public bleed: Bleed;
  public fold: Fold;
  public scaling: PosterScaling;
  public copyKey: string;
  public isInitialized = false;
  public isDisposed = false;
  public lastAddedTextColor: RGBA = DEFAULT_FILL_COLOR;
  public resizePoster: ResizePoster;
  public videoGeneration: PosterVideoGeneration;
  public readonly history: PosterHistory;
  public readonly savePoster: SavePoster;
  public readonly redux: PosterRedux;
  public readonly posterPreview: PosterPreview;

  private playbackEndedListener?: {page: Page; handler: () => void};
  public isTemplateApplied: boolean;
  public inProgressChanges: Record<string, boolean>;
  public isRefreshVisibleCanvasDisabled = false;
  public readonly debouncedUpdateCurrentPageBasedOnVisibility;
  private pageVisibilityObserver: IntersectionObserver | null = null;

  public selectMovedItems(movedItemUIDs: string[]): void {
    const pageIdWithMovedItemUIDsMap = new Map<string, string[]>();

    for (const itemUid of movedItemUIDs) {
      const item = this.getItemForId(itemUid);
      if (!item) {
        continue;
      }

      const pageId = item.page.hashedID;
      if (!pageIdWithMovedItemUIDsMap.has(pageId)) {
        pageIdWithMovedItemUIDsMap.set(pageId, []);
      }
      pageIdWithMovedItemUIDsMap.get(pageId)?.push(itemUid);
    }

    const earliestPageId = this.pages.pageOrder.find((pid) => pageIdWithMovedItemUIDsMap.has(pid));
    if (!earliestPageId) {
      return;
    }

    const uids = pageIdWithMovedItemUIDsMap.get(earliestPageId) ?? [];
    const earliestPage = this.pages.getPage(earliestPageId);
    if (!earliestPage || uids.length === 0) {
      return;
    }

    const fabricObjects: FabricObject[] = [];

    for (const uid of uids) {
      const item = this.getItemForId(uid);
      if (item?.fabricObject) {
        fabricObjects.push(item.fabricObject);
      }
    }

    earliestPage.activeSelection.selectFabricObjects(fabricObjects);
    // The selected page stays current once the selection clears; otherwise the current page
    // would fall back to the page the items were dragged away from.
    this.setCurrentPage(earliestPageId);
  }

  public constructor(htmlElement: HTMLDivElement, modeDetails: PosterModeDetails) {
    super();
    const posterDiv = document.createElement('div');
    posterDiv.className = 'pmw-page-container-parent';
    htmlElement.appendChild(posterDiv);
    this.HTMLElement = posterDiv;

    this.drawing = new PosterDrawing(this);
    this.mediaCrop = new PosterMediaCrop();
    this.history = new PosterHistory(this);
    this.savePoster = new SavePoster(this);
    this.posterPreview = new PosterPreview(this);
    this.redux = new PosterRedux(this);
    this.folder = new Folder(this);
    this.pages = new PosterPages(this);
    this.grid = new Grid(this);
    this.bleed = new Bleed(this);
    this.fold = new Fold(this);
    this.type = new PosterType();
    this.creator = new User();
    this.videoGeneration = new PosterVideoGeneration(this);
    this.scaling = new PosterScaling(this);
    this.resizePoster = new ResizePoster(this);
    this.mode = new PosterMode(modeDetails);
    this.itemsMultiSelect = new ItemsMultiSelect(this);
    this.copyKey = getUniqueString();
    this.isTemplateApplied = false;
    this.inProgressChanges = {};
    Emitter.on('user:loggedIn', this.onUserLoggedIn.bind(this));
    this.initFabricDefaultValues();
    initDefaultFabricControls();
    this.debouncedUpdateCurrentPageBasedOnVisibility = debounce(this.updateCurrentPageBasedOnVisibility.bind(this), 200, {
      leading: false,
      maxWait: 2000,
    });

    this.initPageVisibilityObserverEarlyForLoadingPerformance();
  }

  public async updateAutoSaveOnUserLogin(): Promise<void> {
    try {
      const isAutoSaveEnabled = (await window.PMW.writeLocal('user/isAutosaveEnabled')) as boolean;
      window.PMW.redux.store.dispatch(updateIsAutosaveEnabled(isAutoSaveEnabled));
      if (isAutoSaveEnabled) {
        this.savePoster.initAutoSave();
      }
    } catch (e) {
      console.error(e);
    }
  }

  private onUserLoggedIn(): void {
    const userId = window.PMW.getUserId();
    if (typeof userId === 'number') {
      this.redux.updateCurrentUserId(userId);
      void this.updateAutoSaveOnUserLogin();
    }
  }

  public areItemsModifying(): boolean {
    return (
      Object.keys(window.PMW.redux.store.getState().posterEditor.arePosterItemsMoving).length > 0 ||
      Object.keys(window.PMW.redux.store.getState().posterEditor.arePosterItemsScaling).length > 0 ||
      Object.keys(window.PMW.redux.store.getState().posterEditor.arePosterItemsRotating).length > 0
    );
  }

  /**
   * This represents the total width canvas if we didn't clip it when goes out of visible area
   */
  public getCanvasTotalWidth(): number {
    return this.scaling.scale * this.width;
  }

  /**
   * This represents the total height canvas if we didn't clip it when goes out of visible area
   */
  public getCanvasTotalHeight(): number {
    return this.scaling.scale * this.height;
  }

  public isPosterInGeneration(): boolean {
    if (!window.PMW.currentUserOnPageLoad?.isSuper) {
      return this.ageOfGeneratedOrder === 0;
    }
    return false;
  }

  public isPosterTemplate(): boolean {
    return this.isPublic && !this.isInternal;
  }

  public updateCurrentPageBasedOnVisibility(): void {
    if (getPosterUi().isSinglePageMode()) {
      return;
    }

    const whiteboardContainerElement = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0);
    if (!whiteboardContainerElement) {
      return;
    }

    const firstPage = this.getFirstPage();
    if (!firstPage?.pageContainer) {
      return;
    }
    const firstPageContainerElementHeight = firstPage.pageContainer.getBoundingClientRect().height ?? 0;
    const canvasContainers = Array.from(document.getElementsByClassName('canvas-container')) as HTMLElement[];

    const startPageCheckFrom = firstPageContainerElementHeight > 0 ? Math.floor(whiteboardContainerElement.scrollTop / firstPageContainerElementHeight) : 0;
    // No canvas containers exist yet while updateFromObject reorders pages mid-init (the
    // generator page hits this) — nothing visible to derive an active page from.
    const occludedBottomHeight = getWhiteboardOccludedBottomHeight(whiteboardContainerElement);
    const activePageElement = getChildElementWithMaxVisibleHeight(canvasContainers.slice(startPageCheckFrom), whiteboardContainerElement, occludedBottomHeight);

    const newActivePageId = activePageElement?.closest('.pmw-page-container')?.getAttribute('data-id');

    if (newActivePageId && this.getCurrentPageId() !== newActivePageId) {
      const currentPageBackdrop = this.pages.getPage(this.getCurrentPageId())?.canvasBackdrop;
      if (currentPageBackdrop && getVisibleHeightOfElement(currentPageBackdrop, whiteboardContainerElement, occludedBottomHeight) >= currentPageBackdrop.offsetHeight) {
        return; // Current page is fully visible — preserve it, don't switch on scroll
      }
      this.setCurrentPage(newActivePageId);
    }
  }

  public getFirstPage(): Page {
    return this.pages.pagesHashMap[this.pages.pageOrder[0]];
  }

  public getTemplateId(): string {
    const firstPageId = this.pages.pageOrder[0];
    const firstPage = firstPageId ? this.pages.pagesHashMap[firstPageId] : undefined;
    return firstPage?.templateData?.posterId ?? '';
  }

  public getGalleryTemplateId(): string | undefined {
    const firstPageId = this.pages.pageOrder[0];
    const firstPage = firstPageId ? this.pages.pagesHashMap[firstPageId] : undefined;
    return firstPage?.galleryTemplateData?.posterId;
  }

  public getGalleryTemplateCreatorId(): number {
    const firstPageId = this.pages.pageOrder[0];
    const firstPage = firstPageId ? this.pages.pagesHashMap[firstPageId] : undefined;
    return firstPage?.galleryTemplateData?.idGalleryTemplateCreator ?? 0;
  }

  public onPosterInit(): void {
    this.updatePagesVisibility();
    this.initPageSuspensionListeners();
    this.history.reset();
    this.isInitialized = true;
    this.savePoster.initAutoSave();
    this.redux.updateReduxData();
    this.redux.updateIsPosterInitialized(true);
    this.showPosterInGenerationWarning();
  }

  public isUpdatingFromObject(): boolean {
    return this.inProgressChanges[UPDATE_FROM_OBJECT_KEY] ?? false;
  }

  private showPosterInGenerationWarning(): void {
    if (this.mode.details.type !== PosterModeType.REGEN) {
      if (this.id && this.creator !== null) {
        if (this.isPosterInGeneration()) {
          getPosterUi().openPosterInGenerationWarningModal();
        }
      }
    }
  }

  public getEditLink(): string {
    return window.PMW.util.site_url(`posterbuilder/load/${this.hashedID}`);
  }

  public getWebpageLink(): string {
    return window.PMW.util.site_url(`posterbuilder/view/${this.hashedID}`);
  }

  public getDuration(): number {
    return this.pages.getTotalDuration();
  }

  public dispose(): void {
    this.isDisposed = true;
    this.pageVisibilityObserver?.disconnect();
    this.pageVisibilityObserver = null;
    this.savePoster.onDispose();
    this.posterPreview.onDispose();
    this.redux.onDispose();
    // Before the pages go: an open crop still holds a window keydown listener and the shortcut scope.
    this.mediaCrop.cancelCrop();
    this.pages.dispose();
    this.scaling.dispose();
    this.HTMLElement.innerHTML = '';
  }

  public observePage(page: Page): void {
    this.pageVisibilityObserver?.observe(page.pageContainer);
  }

  public unobservePage(page: Page): void {
    this.pageVisibilityObserver?.unobserve(page.pageContainer);
  }

  public updatePageSuspensionStates(): void {
    if (this.mode.isGeneration()) {
      return;
    }

    if (getPosterUi().isSinglePageMode()) {
      this.suspendAllPagesExceptCurrentAndNeighbours();
    }
  }

  public setCurrentPage(pageId: string): void {
    if (pageId !== this.activePageId && this.pages.getPage(pageId)) {
      this.activePageId = pageId;
      // The highlight follows getCurrentPageId(), so a selected item's page keeps it while the
      // scroll-active page changes underneath.
      this.pages.refreshActivePageHighlight();
      void this.pages.getPage(pageId)?.loadDeferredPageContent();
      this.redux.updateReduxData();
      // The audio multi-selection mirror is slice-level and keyed to the current page —
      // refresh it from the NEW page's class state or the old page's selection leaks across.
      this.redux.updateAudioSelection();
      if (getPosterUi().isSinglePageMode()) {
        this.updatePagesVisibility();
        this.updatePageSuspensionStates();
      }
    }
  }

  public clearItemSelection(): void {
    this.pages.clearSelection();
  }

  public clearSelection(): void {
    this.clearItemSelection();
    const currentPage = this.pages.getPage(this.getCurrentPageId());
    if (currentPage) {
      currentPage.audioClips.unselectAudioPlaylist();
      currentPage.transitions.clearBoundarySelection();
    }
  }

  public deleteItemById(uid: string, undoable = true): void {
    this.pages.getPageWithItemId(uid)?.items.removeItem(uid, undoable);
  }

  public deleteItemByIds(uids: string[], undoable = true): void {
    showLoading('Deleting');
    for (let index = 0; index < uids.length; index += 1) {
      this.deleteItemById(uids[index], undoable ? index === uids.length - 1 : false);
    }
    hideLoading('Deleting');
  }

  /**
   * Exports the freebie design as a s3 URL for the embedded builder. The URL is then returned to the
   * user and passed to the callback of the openEditor or openWizard function
   */
  public async exportDesign(): Promise<void> {
    if (this.isVideo()) {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_video_export_title'),
        text: window.i18next.t('pmwjs_video_export_desc'),
        ctaButton: {
          text: window.i18next.t('pmwjs_download_design'),
          onClick: (): void => {
            getPosterUi().openNewDownloadModal({
              id: 'mobile-download-panel',
              posterObject: this.toObjectForLoad(),
              isOpenedInEditor: true,
              isOpenedInModal: true,
              isOpenedInPrintShop: false,
            });
          },
        },
      });
    } else {
      showLoading('ExportFreeDownload', {
        text: window.i18next.t('pmwjs_exporting'),
      });

      try {
        // TODO: Update the function isWebDownload() as it is checking against product id to determine, which is outdated.
        const isWebdownload = await this.isWebDownload(
          this.id,
          this.isPosterPremium(),
          this.type.name,
          this.getGalleryTemplateId() ?? '',
          this.getTemplateId(),
          this.savePoster.hasUnsavedChanges(),
          this.isVideo()
        );

        const bits = await this.getCurrentPage().pageSnapshot.getFreeDownloadSnapshot(isWebdownload);
        const response = (await window.PMW.writeLocal('posterbuilder/saveExportedPreview', {
          design: bits,
        })) as string;
        window.parent.postMessage(`${EmbeddedEditorMessageType.FREEBIE_URL}~${encodeURI(response)}`, '*');
      } catch (e) {
        console.error(e);
        getPosterUi().openErrorModal({
          message: window.i18next.t('pmwjs_checkout_download_error'),
        });
      } finally {
        hideLoading('ExportFreeDownload');
      }
    }
  }

  /**
   * Gets a boolean value in the data variable denoting whether the design is a web download or not
   */
  public isWebDownload(
    idPoster: string,
    isPremium: boolean,
    posterType: string,
    idGalleryTemplate: string,
    idTemplate: string,
    unsavedChanges: boolean,
    isVideo: boolean
  ): Promise<boolean> {
    return window.PMW.readLocal(`posterbuilder/isWebDownload/${idPoster ?? 0}/${isPremium ? '1' : '0'}?${new Date().getTime()}`, {
      pt: posterType,
      idgt: idGalleryTemplate,
      idt: idTemplate,
      uc: unsavedChanges ? '1' : '0',
      iv: isVideo ? '1' : '0',
    }) as Promise<boolean>;
  }

  public deleteSelection(): void {
    const currentPage = this.getCurrentPage();
    const selectedAudioUIDs = currentPage.audioClips.getSelectedAudioItemUIDs();
    const selectedItemUIDs = currentPage.getSelectedItems().map((item) => item.uid);

    if (selectedAudioUIDs.length === 1 && selectedItemUIDs.length === 0) {
      currentPage.audioClips.deleteSelectedAudioItem();
      return;
    }
    if (selectedAudioUIDs.length > 0) {
      this.#deleteMixedTimelineSelection(currentPage, selectedItemUIDs, selectedAudioUIDs);
      return;
    }
    currentPage.activeSelection.deleteSelectedItems();
    void currentPage.clampCurrentTime();
  }

  /**
   * Deletes every selected timeline bar — canvas items and audio clips — in ONE
   * history entry. Reached only through the timeline's multi/mixed selections;
   * single-owner selections keep their existing delete paths (including the single
   * audio clip's auto-select-next behavior, which a group delete deliberately skips).
   */
  #deleteMixedTimelineSelection(page: Page, itemUids: string[], audioUids: string[]): void {
    for (const uid of itemUids) {
      page.items.removeItem(uid, false);
    }
    page.activeSelection.clearSelection();
    page.audioClips.deleteAudioItems(audioUids);
    commitPosterMutation(page);
    void page.clampCurrentTime();
    page.audioClips.updatePosterTimeIfNeeded();
  }

  public clearSelectedItemPageId(): void {
    this.updateSelectedItemPageId(undefined);
  }

  public updateSelectedItemPageId(pageHashedId: string | undefined): void {
    if (!pageHashedId && this.selectedItemPageId) {
      this.selectedItemPageId = undefined;
      this.pages.refreshActivePageHighlight();
      this.redux.updateReduxData();
      return;
    }

    this.selectedItemPageId = pageHashedId;
    this.pages.refreshActivePageHighlight();
    this.redux.updateReduxData();
  }

  public getCurrentPageId(): string {
    if (this.selectedItemPageId) {
      return this.selectedItemPageId;
    }

    return this.activePageId;
  }

  public getActivePageId(): string {
    return this.activePageId;
  }

  public getCurrentPage(): Page {
    const currentPageId = this.getCurrentPageId();

    if (currentPageId) {
      const page = this.pages.pagesHashMap[currentPageId];
      if (!page) {
        throw new Error(`There is no page for hashedId: ${currentPageId}`);
      }

      return page;
    }
    throw new Error(`No current page`);
  }

  public getBackendObject(): PosterObjectBackend {
    return getPosterBackendObjectFromPoster(this.toObjectForLoad());
  }

  public toObjectForLoad(): PosterLoadObject {
    return {
      hashedID: this.hashedID,
      type: this.type.toObject(),
      userWidth: this.userWidth,
      userHeight: this.userHeight,
      units: this.units,
      version: this.version,
      name: this.name,
      width: this.width,
      height: this.height,
      id: this.id,
      description: this.description,
      isTemplate: this.isTemplate,
      isPublic: this.isPublic,
      isCopyable: this.isCopyable,
      isPurchaseable: this.isPurchaseable,
      createdOn: this.createdOn,
      lastModified: this.lastModified,
      idLastModifier: this.idLastModifier,
      isInternal: this.isInternal,
      seoName: this.seoName,
      creator: this.creator?.toObject(),
      pages: this.pages.toObject(),
    };
  }

  public toObject(): PosterObject {
    return {
      ...this.toObjectForLoad(),
      scaling: this.scaling.toObject(),
      bleed: this.bleed.toObject(),
      folder: this.folder.toObject(),
      duration: this.getDuration(),
      fold: this.fold.toObject(),
      grid: this.grid.toObject(),
      mode: this.mode.toObject(),
      drawing: this.drawing.toObject(),
      copyKey: this.copyKey,
      activePageId: this.activePageId,
      isInitialized: this.isInitialized,
      itemsMultiSelect: this.itemsMultiSelect.toObject(),
      uploadingImages: this.uploadingImages.map((entry) => ({...entry})),
    };
  }

  public override copyVals(posterObject: DeepPartial<PosterObject> = {}): void {
    const {creator, type, scaling, ...obj} = posterObject;
    super.copyVals(obj);
    this.scaling.copyVals(scaling);
    this.type.copyVals(type);
    this.creator?.copyVals(creator);
  }

  private addInProgressChanges(key: string): void {
    this.inProgressChanges[key] = true;
  }

  private removeInProgressChanges(key: string): void {
    delete this.inProgressChanges[key];
  }

  public async updateFromObject(
    posterObject: DeepPartial<PosterObject>,
    {
      updateRedux = true,
      undoable = true,
      fireModifiedEvent = true,
      refreshActiveSelection = false,
      onItemAddFail,
      pageIdsToUpdate,
    }: UpdateFromObjectOpts = {}
  ): Promise<void> {
    this.addInProgressChanges(UPDATE_FROM_OBJECT_KEY);
    const {pages, mode: _mode, folder, grid, fold, bleed, drawing, itemsMultiSelect, ...pageObjectWithoutPages} = posterObject;
    const didDimensionsUpdate = this.areDimensionsDifferentFromObject(posterObject);
    let renderAllCanvases = false;

    this.copyVals({
      ...pageObjectWithoutPages,
    });

    try {
      if (pages) {
        await this.pages.updateFromObject(pages, {
          updateRedux: false,
          undoable: false,
          fireModifiedEvent: false,
          refreshActiveSelection,
          onItemAddFail,
          pageIdsToUpdate,
        });
      }

      if (folder) {
        this.folder.updateFromObject(folder);
      }
      if (grid) {
        this.grid.updateFromObject(grid);
        renderAllCanvases = true;
      }
      if (bleed) {
        this.bleed.updateFromObject(bleed);
        renderAllCanvases = true;
      }
      if (fold) {
        this.fold.updateFromObject(fold);
        renderAllCanvases = true;
      }
      if (drawing) {
        this.drawing.updateFromObject(drawing);
      }
      if (itemsMultiSelect) {
        this.itemsMultiSelect.updateFromObject(itemsMultiSelect);
      }
    } finally {
      this.removeInProgressChanges(UPDATE_FROM_OBJECT_KEY);
    }

    this.validateValues(posterObject, undoable);

    if (undoable) {
      this.history.addPosterHistory();
    }

    if (didDimensionsUpdate && !this.mode.isGeneration()) {
      this.scaling.fitToScreen();
    }

    if (renderAllCanvases) {
      this.renderAllCanvases();
    }

    if (fireModifiedEvent) {
      this.fire('modified');
    }
    if (updateRedux) {
      this.redux.updateReduxData();
    }
  }

  public renderAllCanvases(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.fabricCanvas.requestRenderAll();
    }
  }

  public refreshAllVisibleCanvases(preemptiveRenderAheadBuffer = 0): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.refreshVisibleCanvas(preemptiveRenderAheadBuffer);
    }
  }

  public updateIsTimelineMode(val: boolean): void {
    const page = this.getCurrentPage();

    if (!val) {
      trackTimelineCloseIfOpen();
    }

    this.redux.updateIsTimelineModeOn({
      isTimelineModeOn: val,
      pageIdToScrollToOnWhiteboardResize: page.hashedID,
    });

    this.updatePagesVisibility();
    getPosterUi().scrollToPage(page.hashedID, false);
  }

  public updatePagesVisibility(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      page.updatePageVisibility();
    }
  }

  public isGalleryTemplate(): boolean {
    return this.id === this.getGalleryTemplateId();
  }

  public isSuperUserEditingOthersPoster(): boolean {
    if (window.PMW.currentUserOnPageLoad === undefined) {
      return false;
    }

    return window.PMW.currentUserOnPageLoad.isSuper && window.PMW.currentUserOnPageLoad.id !== this.creator?.id;
  }

  private validateValues(posterObject: DeepPartial<PosterObject>, undoable: boolean): void {
    if (undoable) {
      if (posterObject.name === '') {
        void this.updateFromObject(
          {
            name: 'Untitled',
          },
          {
            undoable: false,
          }
        );
      }
    }
  }

  public isVideo(): boolean {
    return isPosterVideo(this.toObjectForLoad());
  }

  public getSelectedItems(): ItemType[] {
    return this.getCurrentPage().getSelectedItems();
  }

  public hasActiveSelection(): boolean {
    return this.getSelectedItems().length !== 0;
  }

  public getSelectedViews(): FabricObject[] {
    return this.getCurrentPage().activeSelection.getActiveObjects();
  }

  public getItemForId(uid: string): ItemType | undefined {
    return this.pages.getItemForId(uid);
  }

  public getPageForItem(uid: string): Page | undefined {
    for (const page of Object.values(this.pages.pagesHashMap)) {
      if (page.items.getItem(uid)) {
        return page;
      }
    }
    return undefined;
  }

  public getTranscriptItemFromSubtitleId = (idSubtitle: string): TranscriptItem | undefined => {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (item.isTranscript()) {
          if (idSubtitle in item.subtitlesHashmap) {
            return item;
          }
        }
      }
    }
    return undefined;
  };

  private removePlaybackEndedListener(): void {
    if (this.playbackEndedListener) {
      this.playbackEndedListener.page.off('playback:ended', this.playbackEndedListener.handler);
      this.playbackEndedListener = undefined;
    }
  }

  private listenToPagePlaybackEnded(page: Page, handler: () => void): void {
    this.removePlaybackEndedListener();
    page.on('playback:ended', handler);
    this.playbackEndedListener = {page, handler};
  }

  private onSinglePagePlaybackEnded(): void {
    void this.pause();
  }

  private onWebpagePagePlaybackEnded(endedPageId: string): void {
    const endedPageIndex = this.pages.getPageOrder(endedPageId);
    const nextPageId = endedPageIndex !== undefined ? this.pages.pageOrder[endedPageIndex + 1] : undefined;

    if (nextPageId) {
      this.playPage(nextPageId).catch(console.error);
    } else {
      this.loopPoster().catch(console.error);
    }
  }

  private async loopPoster(): Promise<void> {
    const firstPageId = this.pages.pageOrder[0];
    if (firstPageId) {
      await this.playPage(firstPageId);
    }
  }

  private async pauseOtherPages(pageId: string): Promise<void> {
    const promises = [];
    for (const [id, page] of Object.entries(this.pages.pagesHashMap)) {
      if (id !== pageId && page.isPlaying) {
        promises.push(page.pause());
      }
    }
    await Promise.all(promises);
  }

  private async playPage(pageId: string): Promise<void> {
    const page = this.pages.getPage(pageId);
    if (!page) {
      return;
    }

    // Webpage mode defers page content past the eager batch — land the items before playing.
    await page.loadDeferredPageContent();

    await this.pauseOtherPages(pageId);
    this.setCurrentPage(pageId);
    await page.seek(0);
    await page.playPage();

    if (this.mode.isWebpage()) {
      this.listenToPagePlaybackEnded(page, () => {
        this.onWebpagePagePlaybackEnded(pageId);
      });
    } else {
      this.listenToPagePlaybackEnded(page, this.onSinglePagePlaybackEnded.bind(this));
    }
  }

  public getUniqueGettyImageHashedFilenames(excludeItemUids?: Set<string>): Set<string> {
    const hashedFilenames = new Set<string>();

    for (const page of this.pages.getPagesInOrder()) {
      for (const hashedFilename of page.items.getGettyImageHashedFilenames(excludeItemUids)) {
        hashedFilenames.add(hashedFilename);
      }
    }

    return hashedFilenames;
  }

  public getUniqueGettyImageCount(): number {
    return this.getUniqueGettyImageHashedFilenames().size;
  }

  public getUniqueExtractedGettyStickerHashedFilenames(excludeItemUids?: Set<string>): Set<string> {
    const hashedFilenames = new Set<string>();

    for (const page of this.pages.getPagesInOrder()) {
      for (const hashedFilename of page.items.getExtractedGettyStickerHashedFilenames(excludeItemUids)) {
        hashedFilenames.add(hashedFilename);
      }
    }

    return hashedFilenames;
  }

  public getUniqueExtractedGettyStickerCount(): number {
    return this.getUniqueExtractedGettyStickerHashedFilenames().size;
  }

  public updateTitle(): void {
    document.title = `${this.name} | PosterMyWall`;
  }

  public getUniqueGettyVideoHashedFilenames(excludeItemUids?: Set<string>): Set<string> {
    const hashedFilenames = new Set<string>();

    for (const page of this.pages.getPagesInOrder()) {
      for (const hashedFilename of page.items.getGettyVideoHashedFilenames(excludeItemUids)) {
        hashedFilenames.add(hashedFilename);
      }
    }

    return hashedFilenames;
  }

  public getUniqueGettyVideoCount(): number {
    return this.getUniqueGettyVideoHashedFilenames().size;
  }

  public filterByGettyLimits<T>(items: T[], getClassification: (item: T) => GettyAssetClassification | null, opts: {excludeItemUids?: Set<string>} = {}): GettyLimitFilterResult<T> {
    const droppedTypes = new Set<GettyAssetType>();
    const classified = items.map((item) => ({item, classification: getClassification(item)}));
    if (isUserPremium() || classified.every(({classification}) => classification === null)) {
      return {allowed: items, droppedTypes};
    }

    const {excludeItemUids} = opts;
    const reservedKeysByType: Record<GettyAssetType, Set<string>> = {
      [GettyAssetType.IMAGE]: this.getUniqueGettyImageHashedFilenames(excludeItemUids),
      [GettyAssetType.VIDEO]: this.getUniqueGettyVideoHashedFilenames(excludeItemUids),
      [GettyAssetType.EXTRACTED_STICKER]: this.getUniqueExtractedGettyStickerHashedFilenames(excludeItemUids),
    };

    const allowed: T[] = [];
    for (const {item, classification} of classified) {
      if (classification === null) {
        allowed.push(item);
        continue;
      }
      const {gettyType, identityKey} = classification;
      const reservedKeys = reservedKeysByType[gettyType];
      if (!reservedKeys.has(identityKey) && reservedKeys.size >= POSTER_GETTY_LIMIT[gettyType]) {
        droppedTypes.add(gettyType);
        continue;
      }
      reservedKeys.add(identityKey);
      allowed.push(item);
    }

    return {allowed, droppedTypes};
  }

  public isPlaying(): boolean {
    return Object.values(this.pages.pagesHashMap).some((page) => page.clock.isPlaying());
  }

  public async stop(): Promise<void> {
    this.removePlaybackEndedListener();
    await this.pages.stop();
  }

  public async pause(): Promise<void> {
    this.removePlaybackEndedListener();

    await this.pages.pause();
  }

  public setPosterVolume(volume: number): void {
    if (!this.isVideo()) {
      return;
    }

    Howler.volume(volume);
    for (const page of Object.values(this.pages.pagesHashMap)) {
      for (const item of Object.values(page.items.itemsHashMap)) {
        if (item.isVideo() && item.htmlElement instanceof HTMLVideoElement) {
          item.htmlElement.volume = volume;
          item.htmlElement.muted = volume === 0;
        }
        if (item.isSlideshow()) {
          for (const slide of Object.values(item.slides.slidesHashMap)) {
            if (slide.isVideoSlide() && slide.htmlElement instanceof HTMLVideoElement) {
              slide.htmlElement.volume = volume;
              slide.htmlElement.muted = volume === 0;
            }
          }
        }
      }
    }
  }

  public async seekPosterToSelectedAudioItem(): Promise<void> {
    const currentPage = this.getCurrentPage();
    const selectedAudioItem = currentPage.audioClips.getSelectedAudioItem();
    if (selectedAudioItem) {
      const pageTime = currentPage.audioClips.getTimeForAudioItem(selectedAudioItem.uid);
      await currentPage.seek(pageTime);
    }
  }

  public async play(pageId?: string): Promise<void> {
    if (!canPosterBePlayed()) {
      return;
    }

    this.removePlaybackEndedListener();

    try {
      const pageToPlayId = pageId ?? this.getCurrentPageId();
      const page = this.pages.getPage(pageToPlayId);
      if (!page) {
        return;
      }

      await page.loadDeferredPageContent();

      if (!page.isVideo() && !this.mode.isWebpage()) {
        return;
      }

      if (page.hasPlaybackEnded()) {
        await page.seek(0);
      }

      await this.pauseOtherPages(page.hashedID);
      this.setCurrentPage(page.hashedID);
      await page.playPage();

      if (this.mode.isWebpage()) {
        this.listenToPagePlaybackEnded(page, () => {
          this.onWebpagePagePlaybackEnded(page.hashedID);
        });
      } else {
        this.listenToPagePlaybackEnded(page, this.onSinglePagePlaybackEnded.bind(this));
      }
    } catch (e) {
      console.error(e);
      void this.pause();
    }
  }

  public async replayPage(pageId: string, startTimeSeconds = 0): Promise<void> {
    await this.pause();
    const page = this.pages.getPage(pageId);
    if (!page) {
      return;
    }
    this.setCurrentPage(pageId);
    await page.seek(startTimeSeconds);
    await this.play(pageId);
  }

  public getFontsOnPoster(withVariation = false): string[] {
    const pages = Object.values(this.pages.pagesHashMap);
    const fonts = new Set<string>();

    for (const eachPage of pages) {
      for (const font of eachPage.getFontsOnPage(withVariation)) {
        fonts.add(font);
      }
    }

    return Array.from(fonts);
  }

  private areDimensionsDifferentFromObject(posterObject: DeepPartial<PosterObject>): boolean {
    return (posterObject.width !== undefined && posterObject.width !== this.width) || (posterObject.height !== undefined && posterObject.height !== this.height);
  }

  public togglePlayPause(): void {
    if (this.isPlaying()) {
      void this.pause();
    } else {
      void this.play();
    }
  }

  public getCornerPointsArray(): CornerPointsArray {
    return [
      {
        x: 0,
        y: 0,
      },
      {
        x: this.width,
        y: 0,
      },
      {
        x: this.width,
        y: this.height,
      },
      {
        x: 0,
        y: this.height,
      },
    ];
  }

  public deleteItemsNotVisibleOnPoster(undoable = true): void {
    const itemsToDelete = [];
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (!item.isItemVisibleOnPoster() || (item.isSlideshow() && !item.isValid())) {
          itemsToDelete.push(item.uid);
        }
      }
    }

    this.deleteItemByIds(itemsToDelete, undoable);
  }

  public setMenuItemsWrappingInfo(): void {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      for (const [, item] of Object.entries(page.items.itemsHashMap)) {
        if (item.isMenu()) {
          item.wrappingInfo = item.getWrappedLines();
        }
      }
    }
  }

  public isPosterPremium(): boolean {
    for (const [, page] of Object.entries(this.pages.pagesHashMap)) {
      if (page.isPremium()) {
        return true;
      }
    }
    return false;
  }

  public hasClickableItem(): boolean {
    for (const page of this.pages.getPagesInOrder()) {
      if (page.items.hasClickableItem()) {
        return true;
      }
    }
    return false;
  }

  public getAllItems(): ItemType[] {
    let items: ItemType[] = [];
    for (const page of this.pages.getPagesInOrder()) {
      items = items.concat(page.items.getItems());
    }
    return items;
  }

  public getAllImageItems(): ImageItem[] {
    let items: ImageItem[] = [];
    for (const page of this.pages.getPagesInOrder()) {
      items = items.concat(page.items.getImageItems());
    }
    return items;
  }

  public addToUploadingImages(entry: UploadingImageEntry, updateRedux = false): void {
    if (!this.uploadingImages.some((uploadingImage) => uploadingImage.id === entry.id)) {
      this.uploadingImages.push(entry);
      if (updateRedux) {
        this.redux.updateReduxData();
      }
    }
  }

  public removeFromUploadingImages(id: string, updateRedux = false): void {
    const index = this.uploadingImages.findIndex((uploadingImage) => uploadingImage.id === id);
    if (index !== -1) {
      this.uploadingImages.splice(index, 1);
      if (updateRedux) {
        this.redux.updateReduxData();
      }
    }
  }

  public areAnyImagesUploading(): boolean {
    return this.uploadingImages.length > 0;
  }

  public getImageSrcOfUploadingImage(): string | undefined {
    const imageSrcItem = this.getCurrentPage().items.getItem(this.uploadingImages[0]?.id);
    if (imageSrcItem && (imageSrcItem.isImage() || imageSrcItem.isImageSlide() || imageSrcItem.isImageBackground())) {
      return imageSrcItem.uploadingImageData?.dataUrl ?? imageSrcItem.imageElement?.src;
    }
    return undefined;
  }

  private initFabricDefaultValues(): void {
    this.initFabricWebgl();
    if (this.mode.isGeneration()) {
      config.perfLimitSizeTotal = 4194304;
    }
  }

  private initFabricWebgl(): void {
    config.enableGLFiltering = !(isSafari || isIOS) && this.mode.canUseWebGL();
    config.textureSize = this.mode.isGeneration() ? GENERATION_WEBGL_TEXTURE_SIZE : WEBGL_TEXTURE_SIZE;
    const filter = getFilterBackend();
    if (filter instanceof WebGLFilterBackend) {
      filter.canvas.addEventListener('webglcontextlost', () => {
        console.warn('WebGL context lost');
        notifyPuppeteer('webgl-context-lost');
      });
    }
  }

  public startTemplateLoading(): void {
    this.addInProgressChanges(TEMPLATE_PROCESSING_KEY);
  }

  public endTemplateLoading(): void {
    this.isTemplateApplied = true;

    this.removeInProgressChanges(TEMPLATE_PROCESSING_KEY);
  }

  public getPosterTypeObjectForResize(): PosterTypeObject {
    let posterType = this.type.toObject();
    posterType = {
      ...posterType,
      width: this.width,
      height: this.height,
    };

    return posterType;
  }

  public async replacePosterWithBackendPosterObject(backendPosterObjectToReplaceWith: PosterObjectBackend, maintainDimensions = true): Promise<void> {
    const posterLoadObject = getPosterObjectFromPosterBackendObject(backendPosterObjectToReplaceWith);
    return this.replacePoster(posterLoadObject, {maintainDimensions});
  }

  public async replacePoster(
    posterObjectToReplaceWith: PosterLoadObject,
    {maintainDimensions = true, undoable = true, showResizeSuccessMessage = true}: ReplacePosterOpts = {}
  ): Promise<void> {
    this.startTemplateLoading();

    const posterTypeForResize = this.getPosterTypeObjectForResize();
    const isCurrentPosterCustomSize = this.type.isCustomSize();

    const fitToScreenScale = this.scaling.getFitToScreenScale();
    for (const pageObj of Object.values(posterObjectToReplaceWith.pages.pagesHashMap)) {
      pageObj.zoom = fitToScreenScale;
    }

    await this.pages.updateFromObject(posterObjectToReplaceWith.pages, {
      updateRedux: false,
      fireModifiedEvent: false,
      undoable: false,
      refreshActiveSelection: false,
    });

    this.clearSelectedItemPageId();

    const firstPageId = posterObjectToReplaceWith.pages.pageOrder[0];
    if (firstPageId && this.pages.hasPage(firstPageId)) {
      this.setCurrentPage(firstPageId);
    }

    await this.updateFromObject(
      {
        type: posterObjectToReplaceWith.type,
        width: posterObjectToReplaceWith.width,
        height: posterObjectToReplaceWith.height,
        userWidth: posterObjectToReplaceWith.userWidth,
        userHeight: posterObjectToReplaceWith.userHeight,
        units: posterObjectToReplaceWith.units,
      },
      {undoable: false}
    );

    if (maintainDimensions) {
      resizePosterForResizeData(
        {
          isCustomDimension: isCurrentPosterCustomSize,
          posterType: posterTypeForResize,
        },
        undoable,
        showResizeSuccessMessage
      );
    }

    this.endTemplateLoading();
  }

  public async replacePosterWithTemplate(
    posterObjectToReplaceWith: PosterLoadObject,
    {
      maintainDimensions = true,
      undoable = true,
      showResizeSuccessMessage = true,
      originalTemplatePageOrder,
      setTemplateData = true,
      templatePosterId,
      clearGalleryTemplateData = false,
    }: ReplacePosterWithTemplateOpts = {}
  ): Promise<void> {
    this.startTemplateLoading();

    const posterTypeForResize = this.getPosterTypeObjectForResize();
    const isCurrentPosterCustomSize = this.type.isCustomSize();

    const numberOfPagesInCurrentPoster = this.pages.getNumberOfPages();
    const numberOfPagesInPosterToReplaceWith = Object.keys(posterObjectToReplaceWith.pages.pagesHashMap).length;

    if (numberOfPagesInPosterToReplaceWith === 1) {
      const pageIdForPosterBeingAdded = Object.keys(posterObjectToReplaceWith.pages.pagesHashMap)[0];
      const pageBeingReplaced = this.pages.pagesHashMap[this.activePageId];
      await pageBeingReplaced.replacePageWithObject(posterObjectToReplaceWith.pages.pagesHashMap[pageIdForPosterBeingAdded]);

      if (setTemplateData) {
        const sourcePosterId = templatePosterId ?? posterObjectToReplaceWith.id;
        const templatePageId = originalTemplatePageOrder?.[0] ?? pageIdForPosterBeingAdded;
        const firstTemplatePage = posterObjectToReplaceWith.pages.pagesHashMap[pageIdForPosterBeingAdded];
        const idGalleryTemplateCreator = firstTemplatePage?.galleryTemplateData?.idGalleryTemplateCreator ?? 0;
        const templateData = {posterId: sourcePosterId, pageId: templatePageId};
        const galleryTemplateData = clearGalleryTemplateData ? null : {posterId: sourcePosterId, pageId: templatePageId, idGalleryTemplateCreator};
        await pageBeingReplaced.updateFromObject({templateData, galleryTemplateData}, {undoable: false, updateRedux: false, fireModifiedEvent: false});
      }
    } else {
      const fitToScreenScale = this.scaling.getFitToScreenScale();
      for (const pageObj of Object.values(posterObjectToReplaceWith.pages.pagesHashMap)) {
        pageObj.zoom = fitToScreenScale;
      }

      await this.pages.updateFromObject(posterObjectToReplaceWith.pages, {
        updateRedux: false,
        fireModifiedEvent: false,
        undoable: false,
        refreshActiveSelection: false,
      });

      this.clearSelectedItemPageId();

      const firstPageId = posterObjectToReplaceWith.pages.pageOrder[0];
      if (firstPageId && this.pages.hasPage(firstPageId)) {
        this.setCurrentPage(firstPageId);
      }

      if (setTemplateData) {
        const sourcePosterId = templatePosterId ?? posterObjectToReplaceWith.id;
        const templatePageIds = originalTemplatePageOrder ?? posterObjectToReplaceWith.pages.pageOrder;
        const currentPageIds = this.pages.pageOrder;

        const firstNewTemplatePageId = posterObjectToReplaceWith.pages.pageOrder[0];
        const firstTemplatePage = firstNewTemplatePageId ? posterObjectToReplaceWith.pages.pagesHashMap[firstNewTemplatePageId] : undefined;
        const idGalleryTemplateCreator = firstTemplatePage?.galleryTemplateData?.idGalleryTemplateCreator ?? 0;

        for (let i = 0; i < templatePageIds.length && i < currentPageIds.length; i++) {
          const templatePageId = templatePageIds[i];
          const currentPageId = currentPageIds[i];
          const page = this.pages.pagesHashMap[currentPageId];
          if (!page) continue;

          const templateData = {posterId: sourcePosterId, pageId: templatePageId};
          const galleryTemplateData = clearGalleryTemplateData ? null : {posterId: sourcePosterId, pageId: templatePageId, idGalleryTemplateCreator};
          // eslint-disable-next-line no-await-in-loop
          await page.updateFromObject({templateData, galleryTemplateData}, {undoable: false, updateRedux: false, fireModifiedEvent: false});
        }
      }
    }

    if (numberOfPagesInPosterToReplaceWith > 1 || numberOfPagesInCurrentPoster === 1) {
      await this.updateFromObject(
        {
          type: posterObjectToReplaceWith.type,
          width: posterObjectToReplaceWith.width,
          height: posterObjectToReplaceWith.height,
          userWidth: posterObjectToReplaceWith.userWidth,
          userHeight: posterObjectToReplaceWith.userHeight,
          units: posterObjectToReplaceWith.units,
        },
        {undoable: false}
      );
    }

    if (maintainDimensions) {
      resizePosterForResizeData(
        {
          isCustomDimension: isCurrentPosterCustomSize,
          posterType: posterTypeForResize,
        },
        undoable,
        showResizeSuccessMessage,
        false
      );
    }

    this.endTemplateLoading();
  }

  public async addDesignPagesToPoster(designObject: PosterLoadObject, originalDesignPageOrder: string[], designPosterId: string): Promise<void> {
    await this.#addPagesToPosterWithProgress(designObject, (pageObject, i) => {
      const sourcePageId = originalDesignPageOrder[i] ?? designObject.pages.pageOrder[i];
      pageObject.templateData = {posterId: designPosterId, pageId: sourcePageId};
      pageObject.galleryTemplateData = null;
    });
  }

  /**
   * Imports every page of a gallery template onto the current page's timeline — the timeline "+"
   * menu's Templates pick. The page credits the template (and its designer) unless it already
   * credits one. `originalTemplatePageOrder` holds the source page ids from before the uid rewrite.
   */
  public async appendTemplateToCurrentPageTimeline(templateObject: PosterLoadObject, originalTemplatePageOrder: string[]): Promise<void> {
    const firstSourcePageId = templateObject.pages.pageOrder[0];
    const firstTemplatePage = firstSourcePageId ? templateObject.pages.pagesHashMap[firstSourcePageId] : undefined;
    const templateData = {posterId: templateObject.id, pageId: originalTemplatePageOrder[0] ?? ''};
    await this.#appendSourceToCurrentPageTimeline(templateObject, {
      templateData,
      galleryTemplateData: {...templateData, idGalleryTemplateCreator: firstTemplatePage?.galleryTemplateData?.idGalleryTemplateCreator ?? 0},
    });
  }

  /** Imports every page of one of the user's designs onto the current page's timeline — the timeline "+" menu's My designs pick. */
  public async appendDesignToCurrentPageTimeline(designObject: PosterLoadObject, originalDesignPageOrder: string[]): Promise<void> {
    await this.#appendSourceToCurrentPageTimeline(designObject, {templateData: {posterId: designObject.id, pageId: originalDesignPageOrder[0] ?? ''}, galleryTemplateData: null});
  }

  /** Pages are refit to this poster's dimensions first, the same way added design pages are, then tiled after the current background sequence. */
  async #appendSourceToCurrentPageTimeline(source: PosterLoadObject, sourceCredit: TimelineAppendSourceCredit): Promise<void> {
    if (source.width !== this.width || source.height !== this.height) {
      this.resizePoster.resizePagesForAddedDesign(source.pages.pagesHashMap, source.width, source.height, this.width, this.height);
    }
    const pageObjects = source.pages.pageOrder.map((pageId) => source.pages.pagesHashMap[pageId]).filter((pageObject) => pageObject !== undefined);
    await appendPageObjectsToPageTimeline(this.getCurrentPage(), pageObjects, sourceCredit);
  }

  public async replacePosterWithDesign(designObject: PosterLoadObject, originalDesignPageOrder: string[], designPosterId: string): Promise<void> {
    await this.replacePosterWithTemplate(designObject, {
      setTemplateData: true,
      originalTemplatePageOrder: originalDesignPageOrder,
      templatePosterId: designPosterId,
      clearGalleryTemplateData: true,
    });
  }

  public async addTemplatePagesToPoster(templateObject: PosterLoadObject, originalTemplatePageOrder?: string[]): Promise<void> {
    const templatePosterId = templateObject.id;
    const firstSourcePageId = templateObject.pages.pageOrder[0];
    const firstTemplatePage = firstSourcePageId ? templateObject.pages.pagesHashMap[firstSourcePageId] : undefined;
    const idGalleryTemplateCreator = firstTemplatePage?.galleryTemplateData?.idGalleryTemplateCreator ?? 0;

    await this.#addPagesToPosterWithProgress(templateObject, (pageObject, i) => {
      const templatePageId = originalTemplatePageOrder?.[i] ?? templateObject.pages.pageOrder[i];
      pageObject.templateData = {posterId: templatePosterId, pageId: templatePageId};
      pageObject.galleryTemplateData = {posterId: templatePosterId, pageId: templatePageId, idGalleryTemplateCreator};
    });
  }

  async #addPagesToPosterWithProgress(source: PosterLoadObject, perPageMutator?: (page: PageObject, index: number) => void): Promise<void> {
    this.startTemplateLoading();

    const remainingSlots = MAX_NUMBER_OF_PAGES - this.pages.pageOrder.length;
    const pageIdsToAdd = source.pages.pageOrder.slice(0, remainingSlots);
    if (pageIdsToAdd.length === 0) {
      this.endTemplateLoading();
      return;
    }

    if (source.width !== this.width || source.height !== this.height) {
      this.resizePoster.resizePagesForAddedDesign(source.pages.pagesHashMap, source.width, source.height, this.width, this.height);
    }

    const totalPages = pageIdsToAdd.length;
    const firstNewPageId = pageIdsToAdd[0];
    let addedAnyPage = false;
    try {
      for (let i = 0; i < pageIdsToAdd.length; i++) {
        const sourcePage = source.pages.pagesHashMap[pageIdsToAdd[i]];
        if (!sourcePage) {
          continue;
        }
        const pageObject = perPageMutator ? {...sourcePage} : sourcePage;
        perPageMutator?.(pageObject, i);
        getPosterUi().showMessageGrowl({
          key: ADD_DESIGN_PAGES_GROWL_KEY,
          text: `${window.i18next.t('pmwjs_adding')} ${i + 1} of ${totalPages}`,
          type: GROWL_TYPE.NEUTRAL,
          interval: ADD_DESIGN_PAGES_GROWL_INTERVAL,
          showCloseIcon: false,
        });
        // eslint-disable-next-line no-await-in-loop
        await this.pages.addPage(pageObject, {undoable: false, updateRedux: false});
        addedAnyPage = true;
      }

      this.setCurrentPage(firstNewPageId);
      this.history.addPosterHistory();
    } finally {
      dismissAddDesignPagesGrowl();
      if (addedAnyPage) {
        this.redux.updateReduxData();
      }
      this.endTemplateLoading();
    }
  }

  /**
   * Swaps in a poster that is the user's real starting point — e.g. the layered poster that replaces the blank
   * boot poster once "Editable layers" finishes — and makes it the undo floor. A non-undoable replacement on
   * its own leaves the diff-history snapshot pointing at the pre-replacement poster, so the next edit's diff
   * would encode "add every item" and undoing it would wipe the canvas.
   */
  public async replacePosterAsHistoryBaseline(posterObjectToReplaceWith: PosterLoadObject): Promise<void> {
    await this.replacePosterWithVersion(posterObjectToReplaceWith, false);
    this.history.reset();
  }

  public async replacePosterWithVersion(posterObjectToReplaceWith: PosterLoadObject, undoable = false): Promise<void> {
    this.startTemplateLoading();

    const fitToScreenScale = this.scaling.getFitToScreenScale();
    for (const pageObj of Object.values(posterObjectToReplaceWith.pages.pagesHashMap)) {
      pageObj.zoom = fitToScreenScale;
    }

    await this.pages.updateFromObject(posterObjectToReplaceWith.pages, {
      updateRedux: false,
      fireModifiedEvent: false,
      undoable,
      refreshActiveSelection: false,
    });

    for (const [hashId, loadedPage] of Object.entries(posterObjectToReplaceWith.pages.pagesHashMap)) {
      const page = this.pages.pagesHashMap[hashId];
      if (!page) continue;
      await page.updateFromObject(
        {templateData: loadedPage.templateData, galleryTemplateData: loadedPage.galleryTemplateData},
        {undoable: false, updateRedux: false, fireModifiedEvent: false}
      );
    }

    this.clearSelectedItemPageId();

    const firstPageId = posterObjectToReplaceWith.pages.pageOrder[0];
    if (firstPageId && this.pages.hasPage(firstPageId)) {
      this.setCurrentPage(firstPageId);
    }

    await this.updateFromObject(
      {
        type: posterObjectToReplaceWith.type,
        width: posterObjectToReplaceWith.width,
        height: posterObjectToReplaceWith.height,
        userWidth: posterObjectToReplaceWith.userWidth,
        userHeight: posterObjectToReplaceWith.userHeight,
        units: posterObjectToReplaceWith.units,
      },
      {undoable: false}
    );

    this.endTemplateLoading();
  }

  private suspendAllPagesExceptCurrentAndNeighbours(): void {
    const currentIndex = this.pages.pageOrder.indexOf(this.getCurrentPageId());
    for (let i = 0; i < this.pages.pageOrder.length; i++) {
      const page = this.pages.pagesHashMap[this.pages.pageOrder[i]];
      if (!page) {
        continue;
      }

      if (Math.abs(i - currentIndex) <= 1) {
        page.resume();
      } else {
        page.suspend();
      }
    }
  }

  private initPageVisibilityObserver(): void {
    if (this.pageVisibilityObserver) {
      return;
    }

    const whiteboardContainer = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0) ?? null;
    if (!whiteboardContainer) {
      return;
    }

    this.pageVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageId = (entry.target as HTMLElement).dataset.id ?? '';
          const page = this.pages.getPage(pageId);
          if (!page) {
            continue;
          }
          if (entry.isIntersecting) {
            page.resume();
          } else {
            page.suspend();
          }
        }
      },
      {
        root: whiteboardContainer,
        rootMargin: '100% 0px 100% 0px',
        threshold: 0,
      }
    );
  }

  private initPageSuspensionListeners(): void {
    if (this.mode.isGeneration()) {
      return;
    }

    if (getPosterUi().isSinglePageMode()) {
      this.updatePageSuspensionStates();
    } else {
      this.initPageVisibilityObserver();
      for (const page of this.pages.getPagesInOrder()) {
        this.observePage(page);
      }
    }
  }

  private initPageVisibilityObserverEarlyForLoadingPerformance(): void {
    if (!this.mode.isGeneration() && !getPosterUi().isSinglePageMode()) {
      this.initPageVisibilityObserver();
    }
  }
}
